import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Form } from '../data/form';
import { Approval } from '../data/approval';
import { BodyForm } from '../body/body-form';
import { FormListItem } from '../data/form-list-item';
import { Value } from '../data/value';
import { BodyApproved } from '../body/body-approved';
import { Observable } from 'rxjs';
import { OverviewListItem } from '../data/overview-list-item';

@Injectable({
  providedIn: 'root'
})
export class FormService {
  private http = inject(HttpClient);

  controllerURL = '/api/forms';

  getForms(): Observable<Form[]> {
    return this.http.get<Form[]>(environment.apiUrl + this.controllerURL);
  }

  getForm(id: string): Observable<Form> {
    return this.http.get<Form>(environment.apiUrl + this.controllerURL + '/' + id);
  }

  getFormHistory(id: string): Observable<Form[]> {
    return this.http.get<Form[]>(environment.apiUrl + this.controllerURL + '/' + id + '/history');
  }

  getLatestFormId(id: string, dataProtectionOfficer = false): Observable<Value> {
    let params = new HttpParams();
    if (dataProtectionOfficer) {
      params = params.append('dataProtectionOfficer', 'true');
    }
    return this.http.get<Value>(environment.apiUrl + this.controllerURL + '/' + id + '/latest', { params });
  }

  getFormIdByTag(tag: string, dataProtectionOfficer = false): Observable<Value> {
    let params = new HttpParams();
    if (dataProtectionOfficer) {
      params = params.append('dataProtectionOfficer', 'true');
    }
    return this.http.get<Value>(environment.apiUrl + this.controllerURL + '/tag/' + tag, { params });
  }

  getFormApprovals(id: string, costCentre?: string, username?: string, uidNumber?: string): Observable<Approval[]> {
    const params = new HttpParams()
      .append('costCentre', costCentre ?? '')
      .append('username', username ?? '')
      .append('uidNumber', uidNumber ?? '');
    return this.http.get<Approval[]>(environment.apiUrl + this.controllerURL + '/' + id + '/approvals', { params });
  }

  getEditableForms(system?: string): Observable<FormListItem[]> {
    let apiUrl = environment.apiUrl;
    if (system) {
      apiUrl = system;
    }

    return this.http.get<FormListItem[]>(apiUrl + this.controllerURL + '/editable');
  }

  getFormsForMe(): Observable<FormListItem[]> {
    return this.http.get<FormListItem[]>(environment.apiUrl + this.controllerURL + '/my');
  }

  getFormsForApprover(): Observable<FormListItem[]> {
    return this.http.get<FormListItem[]>(environment.apiUrl + this.controllerURL + '/approver');
  }

  getFormsForOperator(): Observable<FormListItem[]> {
    return this.http.get<FormListItem[]>(environment.apiUrl + this.controllerURL + '/operator');
  }

  getFormsForDataProtectionOfficer(): Observable<FormListItem[]> {
    return this.http.get<FormListItem[]>(environment.apiUrl + this.controllerURL + '/data-protection-officer');
  }

  getVisibleForms(): Observable<OverviewListItem[]> {
    return this.http.get<OverviewListItem[]>(environment.apiUrl + this.controllerURL + '/visible');
  }

  getFormsAvailableForGroup(): Observable<FormListItem[]> {
    return this.http.get<FormListItem[]>(environment.apiUrl + this.controllerURL + '/available-for-group');
  }

  getProductiveVisibleForms(): Observable<OverviewListItem[]> {
    return this.http.get<OverviewListItem[]>('https://tools.rbb-online.de/forms-service' + this.controllerURL + '/visible');
  }

  getProductiveVisibleFormsGroupless(): Observable<OverviewListItem[]> {
    return this.http.get<OverviewListItem[]>('https://tools.rbb-online.de/forms-service' + this.controllerURL + '/visible-groupless');
  }

  getFormsWithAlreadyAcceptedPrivacy(): Observable<string[]> {
    return this.http.get<string[]>(environment.apiUrl + this.controllerURL + '/privacy-accepted');
  }

  createForm(body: BodyForm, system?: string): Observable<string> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    let apiUrl = environment.apiUrl;

    if (system) {
      apiUrl = system;
    }

    return this.http.post<string>(apiUrl + this.controllerURL, body, { headers });
  }

  webSocketForm(body: Form, system?: string): Observable<string> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    let apiUrl = environment.apiUrl;

    if (system) {
      apiUrl = system;
    }

    return this.http.post<string>(apiUrl + this.controllerURL + '/websocket', body, { headers });
  }

  updateForm(body: BodyForm, id: string, system?: string): Observable<string> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    let apiUrl = environment.apiUrl;

    if (system) {
      apiUrl = system;
    }

    return this.http.put<string>(apiUrl + this.controllerURL + '/' + id, body, { headers });
  }

  dataProtectionOfficerApproval(body: BodyApproved, id: string): Observable<string> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    return this.http.put<string>(environment.apiUrl + this.controllerURL + '/' + id + '/data-protection-officer', body, { headers });
  }

  deleteForm(id: string): Observable<boolean> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    return this.http.delete<boolean>(environment.apiUrl + this.controllerURL + '/' + id, { headers });
  }
}
